import { useModelLabel } from "@judo/i18n";
import type { Input, PasswordInput } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useState } from "react";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface PasswordInputComponentProps {
	element: PasswordInput | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
}

/**
 * Password input component with visibility toggle.
 */
export function PasswordInputComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
}: PasswordInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const [showPassword, setShowPassword] = useState(false);
	const startAdornment = buildIconAdornment(element.icon);

	return (
		<TextField
			type={showPassword ? "text" : "password"}
			label={label}
			value={value}
			onChange={(e) => onChange?.(e.target.value)}
			error={!!error}
			helperText={error}
			disabled={disabled ?? element.disabled}
			required={element.required}
			fullWidth
			data-testid={testId}
			slotProps={{
				input: {
					readOnly: readOnly ?? element.isReadOnly,
					startAdornment,
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								onClick={() => setShowPassword(!showPassword)}
								edge="end"
								data-testid={`${testId}::toggle-visibility`}
							>
								{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
							</IconButton>
						</InputAdornment>
					),
				},
				inputLabel: {
					shrink: true,
				},
			}}
		/>
	);
}
